import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CommunityPostImageField } from '@/components/community/community-post-image-field';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FormDialog } from '@/components/shared/form-dialog';
import { ImageLightbox } from '@/components/shared/image-lightbox';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { formatDateTime } from '@/utils/format';

interface CommunityPostRow {
  id: string;
  body: string;
  imageUrls: string[];
  status: 'published' | 'hidden' | 'removed';
  openReportCount: number;
  isChurchAuthored: boolean;
  pinned: boolean;
  visibility: 'church' | 'members' | 'visitor' | 'first_timer';
  createdAt: string;
}

type CommunityPostVisibility = CommunityPostRow['visibility'];

const VISIBILITY_OPTIONS: CommunityPostVisibility[] = [
  'members',
  'visitor',
  'first_timer',
  'church',
];

const statusBadgeVariant = (
  status: CommunityPostRow['status'],
): 'success' | 'neutral' | 'danger' => {
  if (status === 'published') return 'success';
  if (status === 'removed') return 'danger';
  return 'neutral';
};

export const CommunityPage = () => {
  const { t } = useTranslation('community');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useChurchStore((state) => state.membership);
  const canPost = hasPermission(membership, PERMISSIONS.COMMUNITY_POST);
  const canModerate = hasPermission(membership, PERMISSIONS.COMMUNITY_MODERATE);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [composerOpen, setComposerOpen] = useState(false);
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const [visibility, setVisibility] = useState<CommunityPostVisibility>('members');
  const [imageKey, setImageKey] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{
    urls: string[];
    index: number;
    caption: string | null;
  } | null>(null);
  const [moderateTarget, setModerateTarget] = useState<{
    post: CommunityPostRow;
    action: 'hide' | 'remove' | 'restore';
  } | null>(null);

  const list = useQuery({
    queryKey: ['community', 'posts', page, pageSize],
    queryFn: () =>
      api.get<{ data: CommunityPostRow[]; page: OffsetPage }>('/community/posts', {
        query: { page, pageSize },
      }),
    placeholderData: keepPreviousData,
  });

  const resetComposer = () => {
    setBody('');
    setPinned(false);
    setVisibility('members');
    setImageKey(null);
    setImagePreviewUrl(null);
  };

  const publish = useMutation({
    mutationFn: () =>
      api.post<CommunityPostRow>('/community/posts', {
        body,
        imageKeys: imageKey ? [imageKey] : [],
        pinned,
        visibility,
      }),
    onSuccess: async () => {
      toast.success(t('post.saved'));
      setComposerOpen(false);
      resetComposer();
      await queryClient.invalidateQueries({ queryKey: ['community'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const moderate = useMutation({
    mutationFn: (input: { postId: string; action: 'hide' | 'remove' | 'restore' }) =>
      api.patch<CommunityPostRow>(`/community/posts/${input.postId}/moderate`, {
        action: input.action,
      }),
    onSuccess: async (_data, variables) => {
      if (variables.action === 'hide') toast.success(t('moderation.hidden'));
      if (variables.action === 'remove') toast.success(t('moderation.removed'));
      if (variables.action === 'restore') toast.success(t('moderation.restored'));
      setModerateTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['community'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;

  const columns: DataTableColumn<CommunityPostRow>[] = useMemo(
    () => [
      {
        id: 'createdAt',
        header: t('columns.createdAt'),
        width: '9rem',
        cell: (row) => (
          <span className="whitespace-nowrap">{formatDateTime(row.createdAt)}</span>
        ),
      },
      {
        id: 'body',
        header: t('columns.body'),
        cell: (row) => (
          <div className="min-w-0">
            <p className="line-clamp-3 whitespace-pre-wrap text-body-sm">{row.body}</p>
            {row.isChurchAuthored ? (
              <p className="mt-1 text-caption text-fg-secondary">{t('post.churchAuthored')}</p>
            ) : null}
          </div>
        ),
      },
      {
        id: 'image',
        header: t('columns.image'),
        width: '4.5rem',
        align: 'center',
        cell: (row) => {
          const imageUrl = row.imageUrls[0];
          if (!imageUrl) return '—';
          return (
            <img
              src={imageUrl}
              alt={t('previewImage')}
              className="mx-auto size-12 cursor-pointer rounded-md object-cover object-top transition-opacity hover:opacity-90"
              onClick={(event) => {
                event.stopPropagation();
                setLightbox({
                  urls: row.imageUrls,
                  index: 0,
                  caption: row.body.trim() || null,
                });
              }}
            />
          );
        },
      },
      {
        id: 'status',
        header: t('columns.status'),
        width: '7rem',
        cell: (row) => (
          <Badge variant={statusBadgeVariant(row.status)}>{t(`status.${row.status}`)}</Badge>
        ),
      },
      {
        id: 'visibility',
        header: t('columns.visibility'),
        width: '8.5rem',
        hideBelowMd: true,
        cell: (row) => t(`post.visibility.${row.visibility}`),
      },
      {
        id: 'reports',
        header: t('columns.reports'),
        width: '8rem',
        hideBelowMd: true,
        cell: (row) =>
          row.openReportCount > 0 ? (
            <Badge variant="danger">
              {t('moderation.reports', { count: row.openReportCount })}
            </Badge>
          ) : (
            '—'
          ),
      },
    ],
    [t],
  );

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        tipId="community.purpose"
        headerActions={
          canPost ? (
            <Button onClick={() => setComposerOpen(true)}>{t('compose')}</Button>
          ) : undefined
        }
      />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={
          <div className="rounded-lg border border-dashed border-border p-8 text-center">
            <p className="text-body font-medium">{t('empty.title')}</p>
            <p className="mt-1 text-caption text-fg-secondary">{t('empty.description')}</p>
            {canPost ? (
              <div className="mt-4 flex justify-end sm:justify-center">
                <Button onClick={() => setComposerOpen(true)}>{t('empty.action')}</Button>
              </div>
            ) : null}
          </div>
        }
        rowActions={
          canModerate
            ? (row) => (
                <div className="flex justify-end gap-2">
                  {row.status === 'published' ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(event) => {
                          event.stopPropagation();
                          setModerateTarget({ post: row, action: 'hide' });
                        }}
                      >
                        {t('moderation.hide')}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(event) => {
                          event.stopPropagation();
                          setModerateTarget({ post: row, action: 'remove' });
                        }}
                      >
                        {t('moderation.remove')}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(event) => {
                        event.stopPropagation();
                        setModerateTarget({ post: row, action: 'restore' });
                      }}
                    >
                      {t('moderation.restore')}
                    </Button>
                  )}
                </div>
              )
            : undefined
        }
        actionsHeader={tCommon('table.actions')}
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) =>
                tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <ConfirmDialog
        open={moderateTarget?.action === 'hide'}
        onOpenChange={(next) => {
          if (!next) setModerateTarget(null);
        }}
        title={t('confirm.hidePost.title')}
        description={t('confirm.hidePost.description')}
        confirmLabel={t('confirm.hidePost.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={moderate.isPending}
        onConfirm={() => {
          if (moderateTarget) {
            moderate.mutate({ postId: moderateTarget.post.id, action: 'hide' });
          }
        }}
      />
      <ConfirmDialog
        open={moderateTarget?.action === 'remove'}
        onOpenChange={(next) => {
          if (!next) setModerateTarget(null);
        }}
        title={t('confirm.removePost.title')}
        description={t('confirm.removePost.description')}
        confirmLabel={t('confirm.removePost.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={moderate.isPending}
        onConfirm={() => {
          if (moderateTarget) {
            moderate.mutate({ postId: moderateTarget.post.id, action: 'remove' });
          }
        }}
      />
      <ConfirmDialog
        open={moderateTarget?.action === 'restore'}
        onOpenChange={(next) => {
          if (!next) setModerateTarget(null);
        }}
        title={t('confirm.restorePost.title')}
        description={t('confirm.restorePost.description')}
        confirmLabel={t('confirm.restorePost.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={moderate.isPending}
        onConfirm={() => {
          if (moderateTarget) {
            moderate.mutate({ postId: moderateTarget.post.id, action: 'restore' });
          }
        }}
      />

      <FormDialog
        open={composerOpen}
        onOpenChange={(open) => {
          setComposerOpen(open);
          if (!open) resetComposer();
        }}
        title={t('compose')}
        description={t('post.placeholder')}
        submitLabel={t('post.publish')}
        cancelLabel={tCommon('action.cancel')}
        onSubmit={() => publish.mutate()}
        submitting={publish.isPending}
        submitDisabled={!body.trim()}
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="post-body">{t('post.placeholder')}</Label>
            <Textarea
              id="post-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
            />
          </div>
          <div className="pt-2">
            <CommunityPostImageField
              idPrefix="community-post"
              imageKey={imageKey}
              previewUrl={imagePreviewUrl}
              onChange={({ imageKey: nextKey, previewUrl }) => {
                setImageKey(nextKey);
                setImagePreviewUrl(previewUrl);
              }}
            />
          </div>
          <div>
            <Label>{t('post.visibility.label')}</Label>
            <Listbox
              value={visibility}
              onChange={(value) => setVisibility(value as CommunityPostVisibility)}
              popoverPortal="dialog"
              options={VISIBILITY_OPTIONS.map((value) => ({
                value,
                label: t(`post.visibility.${value}`),
              }))}
            />
          </div>
          <label className="flex items-center gap-2 text-caption">
            <Switch checked={pinned} onCheckedChange={setPinned} />
            {t('post.pin')}
          </label>
        </div>
      </FormDialog>

      <ImageLightbox
        images={lightbox?.urls ?? []}
        index={lightbox?.index ?? null}
        onClose={() => setLightbox(null)}
        onIndexChange={(index) => {
          setLightbox((current) => (current ? { ...current, index } : null));
        }}
        caption={lightbox?.caption ?? null}
        labels={{
          dialogLabel: tCommon('lightbox.title'),
          close: tCommon('lightbox.close'),
          previous: tCommon('lightbox.previous'),
          next: tCommon('lightbox.next'),
          count:
            lightbox !== null && lightbox.urls.length > 0
              ? tCommon('lightbox.imageCount', {
                  current: lightbox.index + 1,
                  total: lightbox.urls.length,
                })
              : '',
          imageAlt: tCommon('lightbox.title'),
        }}
      />
    </div>
  );
};
